const REQUIRED_TRUE_FIELDS = [
  ['dbs_consent_given', 'DBS consent'],
  ['understands_dbs_required', 'DBS acknowledgement'],
  ['information_accurate_declared', 'Information accuracy confirmation'],
  ['consents_to_reference_checks', 'Reference-check consent'],
  ['consents_to_background_checks', 'Background-check consent'],
  ['consents_to_data_processing', 'Data-processing consent'],
];

const REQUIRED_CHOICE_FIELDS = [
  ['has_criminal_convictions', 'Criminal declaration'],
  ['has_health_conditions', 'Health declaration'],
  ['has_rtw_restrictions', 'Right-to-work restrictions'],
  ['has_driving_licence', 'Driving-licence declaration'],
];

const REQUIRED_TEXT_FIELDS = [
  ['applicant_signature', 'Applicant signature'],
  ['declaration_date', 'Declaration date'],
];

const CONDITIONAL_TEXT_FIELDS = [
  ['has_criminal_convictions', 'criminal_convictions_details', 'Criminal-conviction details'],
  ['has_health_conditions', 'health_conditions_details', 'Health-condition details'],
  ['has_rtw_restrictions', 'rtw_restrictions_details', 'Right-to-work restriction details'],
  ['has_rtw_restrictions', 'rtw_expiry_date', 'Right-to-work expiry date'],
];

function submissionData(submission) {
  if (!submission || typeof submission !== 'object') return {};
  const formData = submission.form_data;
  const data = submission.data;
  if (formData && typeof formData === 'object') return formData;
  if (data && typeof data === 'object') return data;
  return {};
}

function truthy(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return ['true', '1', 'yes', 'y', 'on', 'confirmed'].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
}

function hasYesNoValue(value) {
  if (typeof value === 'boolean') return true;
  if (typeof value === 'string') {
    return ['true', 'false', '1', '0', 'yes', 'no', 'y', 'n'].includes(value.trim().toLowerCase());
  }
  return false;
}

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

export function getApplicantDeclarationMissingFields(submission) {
  const data = submissionData(submission);
  const missing = [];

  REQUIRED_TRUE_FIELDS.forEach(([key, label]) => {
    if (!truthy(data[key])) missing.push({ key, label });
  });

  REQUIRED_CHOICE_FIELDS.forEach(([key, label]) => {
    if (!hasYesNoValue(data[key])) missing.push({ key, label });
  });

  REQUIRED_TEXT_FIELDS.forEach(([key, label]) => {
    if (!hasText(data[key])) missing.push({ key, label });
  });

  CONDITIONAL_TEXT_FIELDS.forEach(([triggerKey, detailKey, label]) => {
    if (truthy(data[triggerKey]) && !hasText(data[detailKey])) {
      missing.push({ key: detailKey, label });
    }
  });

  const seen = new Set();
  return missing.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
}

export function deriveApplicantDeclarationDisplayState({
  declarationsOnFile = false,
  declarationsAdequatelyReviewed = false,
  declarationSubmission = null,
  employee = null,
} = {}) {
  const submittedAt =
    employee?.declarations?.applicant_declaration_submitted_at ||
    employee?.declarations?.applicant_submitted_at ||
    declarationSubmission?.submitted_at ||
    declarationSubmission?.created_at ||
    null;

  // A dedicated declaration record must be complete before we call it
  // "submitted". This catches draft/legacy/partial records that previously got
  // collapsed into the generic "Declarations needed" / "Needs review" states.
  if (declarationSubmission) {
    const missingFields = getApplicantDeclarationMissingFields(declarationSubmission);
    if (missingFields.length > 0) {
      return {
        key: 'incomplete',
        label: 'Incomplete submission',
        tone: 'danger',
        submittedAt,
        missingFields,
        message: `Missing: ${missingFields.map((item) => item.label).join(', ')}.`,
      };
    }
  }

  if (declarationsAdequatelyReviewed) {
    return {
      key: 'reviewed',
      label: 'Reviewed',
      tone: 'success',
      submittedAt,
      missingFields: [],
      message: 'Applicant declarations are on file and have been reviewed.',
    };
  }

  if (declarationsOnFile) {
    return {
      key: 'awaiting_review',
      label: 'Submitted — awaiting review',
      tone: 'warning',
      submittedAt,
      missingFields: [],
      message: 'Applicant declarations are on file. Staff review is still required.',
    };
  }

  if (declarationSubmission) {
    return {
      key: 'submitted_unlinked',
      label: 'Submitted — needs system check',
      tone: 'danger',
      submittedAt,
      missingFields: [],
      message: 'A complete declaration submission exists, but it is not linked to the recruitment review. Do not ask the applicant to resubmit yet.',
    };
  }

  return {
    key: 'not_submitted',
    label: 'Not submitted',
    tone: 'neutral',
    submittedAt: null,
    missingFields: [],
    message: 'No declaration submission was found. Ask the applicant to complete the declarations step.',
  };
}
